import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Select } from "@repo/o37-ui";
import { ArrowLeft, Save, FileText, Bell } from "lucide-react";
import { TermSheetSettings as TermSheetSettingsType } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function TermSheetSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [settings, setSettings] = useState<TermSheetSettingsType>({
    defaultLiquidationPreference: 1.0,
    defaultProRataRights: true,
    defaultAntiDilution: 'broad_based',
    defaultBoardSeats: 0,
    requirePreMoneyValuation: false,
    requireDocumentUrl: false,
    enableNotifications: false,
    expirationDays: 30
  });

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/settings/term-sheets`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (e) {
      console.error('Failed to load settings:', e);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/settings/term-sheets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole,
          'x-admin-email': userId // For admin check
        },
        body: JSON.stringify({ settings })
      });

      if (response.ok) {
        setSuccess('Settings saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save settings');
      }
    } catch (e) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Loading...</h1>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          icon={<ArrowLeft className="w-4 h-4" />}
          text="Back"
          className="mb-4"
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Term Sheet Settings
            </h1>
            <p className="text-neutral-500 mt-1">
              Configure default values and requirements for term sheets
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            icon={<Save className="w-4 h-4" />}
            text={saving ? 'Saving...' : 'Save Settings'}
          />
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-200">
          {success}
        </div>
      )}

      <div className="space-y-6 max-w-4xl">
        {/* Default Values */}
        <Card>
          <CardHeader>
            <CardTitle>Default Values</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <p className="text-sm text-neutral-600 mb-4">
              These values will be pre-filled when creating new term sheets, saving time and ensuring consistency.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Default Liquidation Preference
                </label>
                <div className="relative">
                  <Input
                    value={settings.defaultLiquidationPreference?.toString() || '1'}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      defaultLiquidationPreference: parseFloat(e.target.value) || 1 
                    })}
                    placeholder="1.0"
                    type="number"
                    step="0.1"
                    min="0"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">x</span>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Typical value is 1x (investor gets their money back first)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Default Board Seats
                </label>
                <Input
                  value={settings.defaultBoardSeats?.toString() || '0'}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    defaultBoardSeats: parseInt(e.target.value) || 0 
                  })}
                  placeholder="0"
                  type="number"
                  min="0"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Number of board seats typically requested
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Default Anti-Dilution Protection
              </label>
              <Select 
                value={settings.defaultAntiDilution || 'broad_based'}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  defaultAntiDilution: e.target.value as any 
                })}
              >
                <option value="broad_based">Broad-Based Weighted Average</option>
                <option value="narrow_based">Narrow-Based Weighted Average</option>
                <option value="full_ratchet">Full Ratchet</option>
                <option value="none">None</option>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">
                Broad-based is most founder-friendly, full ratchet is most investor-friendly
              </p>
            </div>

            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <input
                type="checkbox"
                id="proRataRights"
                checked={settings.defaultProRataRights !== false}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  defaultProRataRights: e.target.checked 
                })}
                className="rounded border-neutral-300"
              />
              <label htmlFor="proRataRights" className="text-sm font-medium text-neutral-700 cursor-pointer">
                Enable Pro-Rata Rights by Default
              </label>
            </div>
            <p className="text-xs text-neutral-500 -mt-4 ml-7">
              Allows investors to maintain their ownership percentage in future rounds
            </p>
          </CardContent>
        </Card>

        {/* Validation Requirements */}
        <Card>
          <CardHeader>
            <CardTitle>Validation Requirements</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-neutral-600 mb-4">
              Enforce required fields when creating or editing term sheets.
            </p>

            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
                <input
                  type="checkbox"
                  id="requireValuation"
                  checked={settings.requirePreMoneyValuation}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    requirePreMoneyValuation: e.target.checked 
                  })}
                  className="rounded border-neutral-300"
                />
                <label htmlFor="requireValuation" className="text-sm font-medium text-neutral-700 cursor-pointer flex-1">
                  Require Pre-Money Valuation
                </label>
              </div>

              <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
                <input
                  type="checkbox"
                  id="requireDoc"
                  checked={settings.requireDocumentUrl}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    requireDocumentUrl: e.target.checked 
                  })}
                  className="rounded border-neutral-300"
                />
                <label htmlFor="requireDoc" className="text-sm font-medium text-neutral-700 cursor-pointer flex-1">
                  Require Document URL
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Default Expiration Days
                </label>
                <Input
                  value={settings.expirationDays?.toString() || '30'}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    expirationDays: parseInt(e.target.value) || 30 
                  })}
                  placeholder="30"
                  type="number"
                  min="1"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Default number of days until term sheets expire
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-neutral-600 mb-4">
              Get notified when term sheets are received or about to expire.
            </p>

            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <input
                type="checkbox"
                id="enableNotifications"
                checked={settings.enableNotifications}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  enableNotifications: e.target.checked 
                })}
                className="rounded border-neutral-300"
              />
              <label htmlFor="enableNotifications" className="text-sm font-medium text-neutral-700 cursor-pointer flex-1">
                Enable Email Notifications
              </label>
            </div>

            {settings.enableNotifications && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Notification Email
                </label>
                <Input
                  value={settings.notificationEmail || ''}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    notificationEmail: e.target.value 
                  })}
                  placeholder="your-email@company.com"
                  type="email"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Leave empty to use your account email
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button at Bottom */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            icon={<Save className="w-4 h-4" />}
            text={saving ? 'Saving...' : 'Save Settings'}
          />
        </div>
      </div>
    </div>
  );
}
